import type L from "leaflet";
import type { LatLng } from "leaflet";
import type { BookTypeEng } from "@/types/acquirebook";
import type { PropertySelectionRow } from "../../types";
import { normalizeLocation, normalizeChibanKaokuNumber } from "../index";
import {
  SAME_POSITION_MARGIN,
  findPropertyRowIdAtPosition,
  getPropertyRowPosition,
} from "./propertyRowPosition";

/**
 * ピンが表している物件。物件一覧の行と同じ種別・都道府県・所在・地番（家屋番号）で持つ。
 * 区分建物など同じ座標に別の物件のピンが並ぶことがあるので、座標ではなくこれで行と突き合わせる
 */
export interface PinProperty {
  bookType: BookTypeEng;
  prefName: string;
  location: string;
  chibanKaokuNumber: string;
}

/** ピン（CanvasPinMarker の options.property）が表している物件。持たないピンは undefined */
export const getPinProperty = (layer: L.Layer): PinProperty | undefined =>
  (layer.options as { property?: PinProperty } | undefined)?.property;

/** 物件一覧の行が、ピンの物件かどうか（所在・地番は表記ゆれを正規化して比べる） */
export const isRowOfPinProperty = (
  row: PropertySelectionRow,
  property: PinProperty
): boolean =>
  row.bookType === property.bookType &&
  row.prefName === property.prefName &&
  normalizeLocation(row.location) === normalizeLocation(property.location) &&
  normalizeChibanKaokuNumber(row.chibanKaokuNumber) ===
    normalizeChibanKaokuNumber(property.chibanKaokuNumber);

/**
 * ポップアップを開いたピンに対応する、物件一覧の行の id。無ければ null。
 *
 * ピンが物件を持っていれば、その物件の行だけを返す（同じ座標の別の物件の行は選ばない）。
 * 持っていなければ座標で引く。その地点にいま色を付けている行があれば、それを優先する
 * （同じ座標に行が並ぶ時、押した行から別の行へ色が移らないように）
 */
export const findPropertyRowIdForPin = (
  rows: PropertySelectionRow[],
  position: LatLng,
  property: PinProperty | undefined,
  focusedRowId: number | null
): number | null => {
  if (property) {
    return rows.find((r) => isRowOfPinProperty(r, property))?.id ?? null;
  }
  const focused = rows.find((r) => r.id === focusedRowId);
  if (
    focused &&
    getPropertyRowPosition(focused)?.equals(position, SAME_POSITION_MARGIN)
  ) {
    return focused.id;
  }
  return findPropertyRowIdAtPosition(rows, position);
};
